import type { PassportPageSummary } from "@peer-start/platform-contracts";
import { Card, CardHeader, ProgressBar, ProgressRing } from "@peer-start/ui";
import { useTranslation } from "react-i18next";

export function CompetencyProgress({
  summary,
  position,
}: {
  summary: PassportPageSummary;
  position?: string;
}) {
  const { t } = useTranslation();
  const hasTasks = summary.taskTotal > 0;
  const progressValue = hasTasks || summary.required ? summary.progressPercent : null;
  return (
    <Card>
      <CardHeader
        title={t("learningRoute.competency", { title: summary.page.title })}
        action={
          position ? (
            <span className="whitespace-nowrap rounded-full border border-border px-2 py-1 text-xs text-muted-foreground">
              {position}
            </span>
          ) : null
        }
      />
      <ProgressRing
        label={t("learningRoute.competency", { title: summary.page.title })}
        value={progressValue}
        caption={t(
          summary.required === 0
            ? "learningRoute.state.optional"
            : summary.complete
              ? "learningRoute.state.complete"
              : "learningRoute.onTrack",
        )}
      />
      <p className="text-center text-sm text-muted-foreground">
        {hasTasks
          ? t("learningRoute.completedTasks", {
              count: summary.taskCompleted,
              total: summary.taskTotal,
            })
          : t(
              summary.required === 0
                ? "learningRoute.optionalProgress"
                : summary.required === 1
                  ? "learningRoute.completedPart"
                  : "learningRoute.completedParts",
              {
                count: summary.confirmed,
                total: summary.required,
              },
            )}
      </p>
    </Card>
  );
}

export function CompetencyBars({ pages }: { pages: PassportPageSummary[] }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader title={t("learningRoute.competencies")} />
      <div className="space-y-4">
        {pages.map((summary) => (
          <ProgressBar
            key={summary.page.id}
            label={summary.page.title}
            value={summary.taskTotal > 0 || summary.required ? summary.progressPercent : null}
            layout="inline"
          />
        ))}
      </div>
    </Card>
  );
}

export function LearningRouteProgress({
  pages,
  current,
}: {
  pages: PassportPageSummary[];
  current?: PassportPageSummary;
}) {
  return (
    <>
      {current ? (
        <CompetencyProgress summary={current} position={`${pages.indexOf(current) + 1} / ${pages.length}`} />
      ) : null}
      <CompetencyBars pages={pages} />
    </>
  );
}
